import React, { useState, useEffect } from 'react';
import { getAllRoomTypes } from '../../api';
import {
  getSeasonalPricingByYear,
  bulkUpdateSeasonalPricing,
  deleteSeasonalPricingByYear
} from '../../api/seasonalPricing';
import '../../styles/main.css';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const SeasonalPricing = () => {
  const [roomTypes, setRoomTypes] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedRoomType, setSelectedRoomType] = useState(null);
  const [seasonalData, setSeasonalData] = useState({});
  const [editingPrices, setEditingPrices] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchRoomTypes();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      fetchSeasonalPricing();
    }
  }, [selectedYear]);

  const fetchRoomTypes = async () => {
    try {
      const data = await getAllRoomTypes();
      setRoomTypes(data);
      if (data.length > 0 && !selectedRoomType) {
        setSelectedRoomType(data[0].id);
      }
    } catch (error) {
      console.error('Error fetching room types:', error);
      showMessage('Failed to load room types', 'error');
    }
  };

  const fetchSeasonalPricing = async () => {
    setLoading(true);
    try {
      const data = await getSeasonalPricingByYear(selectedYear);
      
      // Build a map of prices
      const priceMap = {};
      data.room_types.forEach(rt => {
        priceMap[rt.room_type_id] = {
          base_price: rt.base_price,
          months: rt.months
        };
      });
      
      setSeasonalData(priceMap);
      
      // Initialize editing state
      const initialEditing = {};
      data.room_types.forEach(rt => {
        if (!initialEditing[rt.room_type_id]) {
          initialEditing[rt.room_type_id] = {};
        }
        rt.months.forEach(m => {
          initialEditing[rt.room_type_id][m.month] = m.price_per_night;
        });
      });
      setEditingPrices(initialEditing);
    } catch (error) {
      console.error('Error fetching seasonal pricing:', error);
      showMessage('Failed to load seasonal pricing', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  const handlePriceChange = (roomTypeId, month, value) => {
    setEditingPrices(prev => ({
      ...prev,
      [roomTypeId]: {
        ...prev[roomTypeId],
        [month]: value
      }
    }));
  };

  const handleSaveRoomType = async (roomTypeId) => {
    setSaving(true);
    try {
      const monthsData = [];
      for (let month = 1; month <= 12; month++) {
        const price = editingPrices[roomTypeId]?.[month];
        if (price) {
          monthsData.push({
            month,
            price_per_night: parseFloat(price)
          });
        }
      }
      
      await bulkUpdateSeasonalPricing(roomTypeId, selectedYear, monthsData);
      await fetchSeasonalPricing();
      showMessage('Seasonal pricing saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving seasonal pricing:', error);
      showMessage(error.message || 'Failed to save seasonal pricing', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToBasePrices = (roomTypeId) => {
    const basePrice = seasonalData[roomTypeId]?.base_price || 0;
    setEditingPrices(prev => ({
      ...prev,
      [roomTypeId]: Object.fromEntries(
        Array.from({ length: 12 }, (_, i) => [i + 1, basePrice])
      )
    }));
  };

  const handleDeleteYear = async (roomTypeId) => {
    if (!window.confirm(`Are you sure you want to delete all seasonal pricing for ${selectedYear}?`)) {
      return;
    }
    
    setSaving(true);
    try {
      await deleteSeasonalPricingByYear(roomTypeId, selectedYear);
      await fetchSeasonalPricing();
      showMessage('Seasonal pricing deleted successfully!', 'success');
    } catch (error) {
      console.error('Error deleting seasonal pricing:', error);
      showMessage(error.message || 'Failed to delete seasonal pricing', 'error');
    } finally {
      setSaving(false);
    }
  };

  const getCurrentRoomType = () => {
    return roomTypes.find(rt => rt.id === selectedRoomType);
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear + i);
  };

  return (
    <div className="seasonal-pricing-container">
      <div className="page-header">
        <h2>Seasonal Pricing Management</h2>
        <p>Set monthly pricing for each room type. If no seasonal price is set, the base room price will be used.</p>
      </div>

      {message && (
        <div className={`notification ${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="filters-section">
        <div className="filter-group">
          <label>Year:</label>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="filter-select"
          >
            {getYearOptions().map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Room Type:</label>
          <select
            value={selectedRoomType || ''}
            onChange={(e) => setSelectedRoomType(parseInt(e.target.value))}
            className="filter-select"
          >
            {roomTypes.map(rt => (
              <option key={rt.id} value={rt.id}>
                {rt.name} (Base: ₱{rt.price_per_night})
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading seasonal pricing...</div>
      ) : (
        selectedRoomType && (
          <div className="pricing-editor">
            <div className="editor-header">
              <div>
                <h3>{getCurrentRoomType()?.name}</h3>
                <p className="base-price-info">
                  Base Price: ₱{seasonalData[selectedRoomType]?.base_price?.toLocaleString()}
                </p>
              </div>
              <div className="editor-actions">
                <button
                  onClick={() => handleResetToBasePrices(selectedRoomType)}
                  className="btn-secondary"
                  disabled={saving}
                >
                  Reset to Base Prices
                </button>
                <button
                  onClick={() => handleDeleteYear(selectedRoomType)}
                  className="btn-danger"
                  disabled={saving}
                >
                  Delete All for {selectedYear}
                </button>
                <button
                  onClick={() => handleSaveRoomType(selectedRoomType)}
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>

            <div className="months-grid">
              {MONTHS.map((monthName, index) => {
                const month = index + 1;
                const monthData = seasonalData[selectedRoomType]?.months?.[index];
                const isSeasonal = monthData?.is_seasonal;
                const currentPrice = editingPrices[selectedRoomType]?.[month] || monthData?.price_per_night || 0;

                return (
                  <div key={month} className={`month-card ${isSeasonal ? 'is-seasonal' : ''}`}>
                    <div className="month-header">
                      <span className="month-name">{monthName}</span>
                      {isSeasonal && <span className="seasonal-badge">Seasonal</span>}
                    </div>
                    <div className="month-body">
                      <label>Price per Night:</label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={currentPrice}
                        onChange={(e) => handlePriceChange(selectedRoomType, month, e.target.value)}
                        className="price-input"
                      />
                      <div className="price-display">
                        ₱{parseFloat(currentPrice).toLocaleString()}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )
      )}

      <div className="info-section">
        <h4>How Seasonal Pricing Works:</h4>
        <ul>
          <li>Set monthly prices for each room type for specific years</li>
          <li>When guests book, the system will use the seasonal price if available for that month/year</li>
          <li>If no seasonal price is set, the base room price is used automatically</li>
          <li>Prices are applied based on the check-in date of the reservation</li>
          <li>You can set prices for multiple years in advance</li>
        </ul>
      </div>
    </div>
  );
};

export default SeasonalPricing;
